import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { AuthLayout, ErrorAlert } from "../components/AuthLayout";
import { LabeledInput } from "../components/AuthFields";
import { Turnstile } from "../components/Turnstile";
import { secureAuth } from "../lib/authApi";
import { appUrl } from "../lib/config";
import { EMAIL_RE } from "../lib/validation";
import { useNoindex } from "../hooks/useNoindex";

export default function ForgotPassword() {
  useNoindex();
  const [email, setEmail] = useState("");
  const [token, setToken] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [sent, setSent] = useState("");
  const ts = useRef(null);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!EMAIL_RE.test(email.trim())) return setError("Please enter a valid email address.");
    if (!token) return setError("Please complete the security check.");
    setBusy(true);
    try {
      const res = await secureAuth("reset", { email: email.trim(), turnstileToken: token, redirectTo: appUrl("/reset-password") });
      setSent(res.message);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
      ts.current?.reset();
    }
  };

  return (
    <AuthLayout testId="forgot-password-page" eyebrow="Account help" title="Reset your password" subtitle="We'll email you a secure link to set a new password.">
      {sent ? (
        <p data-testid="reset-sent-message" className="rounded-lg bg-[#F5E6BA]/60 px-4 py-3 text-sm text-[#4A000E]">{sent}</p>
      ) : (
        <form onSubmit={submit} noValidate className="space-y-4">
          <LabeledInput id="email" label="Email address" type="email" value={email} onChange={(e) => setEmail(e.target.value)} testId="reset-email-input" />
          <Turnstile ref={ts} onToken={setToken} action="reset" />
          <ErrorAlert message={error} />
          <Button type="submit" disabled={busy} data-testid="reset-submit-button" className="h-11 w-full rounded-full bg-[#7A0016] font-semibold text-[#F5E6BA] hover:bg-[#4A000E]">
            {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Send reset link
          </Button>
        </form>
      )}
      <p className="mt-6 text-center text-sm">
        <Link to="/login" data-testid="back-to-login-link" className="font-semibold text-[#7A0016] hover:underline">Back to sign in</Link>
      </p>
    </AuthLayout>
  );
}
